import React from 'react';
import Spinner from './Spinner';

/**
 * Shared button. Replaces the hand-rolled `cursor:'pointer'` style objects
 * scattered across the app so variants, sizing, and touch targets live in one
 * place. Global `button:hover { opacity: .9 }` (App.css) handles hover.
 *
 *   variant: primary | secondary | ghost | danger
 *   size:    sm | md   (md keeps a >=40px touch target for mobile)
 *   loading: shows a Spinner and disables the button
 *   fullWidth: stretch to container width
 *
 * Any other props (onClick, type, title, aria-*, disabled, style) pass through;
 * `style` is merged last so callers can still tweak individual instances.
 */
const VARIANTS = {
  primary:   { background: 'var(--teal)',    color: 'var(--bg)',   border: '1px solid var(--teal)' },
  secondary: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' },
  ghost:     { background: 'var(--card)',    color: 'var(--teal)', border: '1px solid var(--border)' },
  danger:    { background: 'var(--red)',     color: '#fff',        border: '1px solid var(--red)' },
};

const SIZES = {
  sm: { padding: '6px 10px', fontSize: 'var(--fs-sm)', minHeight: 30 },
  md: { padding: '9px 14px', fontSize: 'var(--fs-md)', minHeight: 40 },
};

export default function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  children,
  style = {},
  ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.secondary;
  const s = SIZES[size] || SIZES.md;
  const isDisabled = disabled || loading;

  return (
    <button
      disabled={isDisabled}
      style={{
        ...v,
        ...s,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        borderRadius: 'var(--radius-md)',
        fontWeight: 700,
        fontFamily: 'var(--font-body)',
        cursor: isDisabled ? (loading ? 'wait' : 'not-allowed') : 'pointer',
        opacity: isDisabled && !loading ? 0.5 : 1,
        width: fullWidth ? '100%' : undefined,
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {loading && <Spinner size={13} />}
      {children}
    </button>
  );
}
